import { formatKopecks, type PickerRowVm } from '@krp/sale-ui'
import { isVatCode } from '../../sale/model/vat'
import { profileBlockReasons, profileReasonText, type ProfileBlockReason } from '../../sale/model/profile'
import { type SaleProduct } from '../../sale/model/cart'

import type { OwnNotice } from '../../operation/model/words'
import type { OwnSaleLineItem } from '../api/linesContracts'
import type { OwnSaleLine } from './pickerRows'

/**
 * Строка продажи из ответа службы. Продавать можно, только если нет проблемы строки, есть цена и ставка НДС определена (`set` и код,
 * который знает агент). Причина иначе — словами: куда идти и что делать. Ничего не подставляется: нет цены — не 0, нет ставки — не «без НДС».
 */
export const NOT_FOUND_REASON = 'Товар не найден в собственной номенклатуре или убран в архив'
export const NO_PRICE_REASON = 'Нет цены в выбранном виде цены. Выберите другой вид цены или задайте цену во вкладке «Цены» раздела «Номенклатура»'
export const NO_VAT_REASON = 'У товара не указана ставка НДС. Задайте её во вкладке «Настройка» раздела «Номенклатура»'
export const UNMAPPED_VAT_REASON = 'Ставка НДС товара не из тех, что принимает касса. Исправьте её во вкладке «Настройка» раздела «Номенклатура»'
export const UNAVAILABLE_VAT_REASON = 'Ставка НДС недоступна: карточку товара прочитать не удалось'

export const MARKED_REASON = 'Товар маркированный, а касса пока не принимает маркировку. Уберите его из чека'
export const PROFILE_MISSING_REASON = 'У товара не заполнены кассовые реквизиты (предмет и способ расчёта, маркировка, единица). Задайте их в карточке товара во вкладке «Настройка» раздела «Номенклатура»'

const fixInCard = (what: string): string => `У товара ${what}. Исправьте карточку товара во вкладке «Настройка» раздела «Номенклатура»`

/** Кассовые поля карточки по-русски (`undefinedFields`, имена полей общего товара); неизвестное имя остаётся как есть. */
const FIELD_TEXTS: Readonly<Record<string, string>> = {
  vatRate: 'ставка НДС',
  unitCode: 'единица измерения',
  unitName: 'имя единицы измерения',
  isWeighted: 'весовой признак',
  paymentSubject: 'предмет расчёта',
  paymentMethod: 'способ расчёта',
}

/**
 * Слова службы о блокировке строки (`blockReasons`), когда готового текста службы нет; неизвестное слово остаётся как есть, а не
 * пропадает. `cash-field-undefined` (RP-SY5) называет поля из `undefinedFields`.
 */
function serverReasonText(reason: string, undefinedFields: readonly string[]): string {
  switch (reason) {
    case 'cash-field-undefined':
      return `не определены кассовые поля${undefinedFields.length === 0 ? '' : `: ${undefinedFields.map((field) => FIELD_TEXTS[field] ?? field).join(', ')}`}`
    case 'vat-unmapped':
      return 'ставка НДС не из словаря чека'
    case 'payment-subject-unsupported':
    case 'marked-not-supported':
      return profileReasonText(reason)
    default:
      return reason
  }
}

function profileReason(reasons: readonly ProfileBlockReason[]): string {
  if (reasons.length === 1 && reasons[0] === 'profile-missing') return PROFILE_MISSING_REASON
  if (reasons.length === 1 && reasons[0] === 'marked-not-supported') return MARKED_REASON
  return fixInCard(reasons.map(profileReasonText).join(', '))
}

/** Почему строку продавать нельзя; `null` — можно. */
export function blockedReasonOf(item: OwnSaleLineItem): string | null {
  if (item.problem === 'product-not-found') return NOT_FOUND_REASON
  if (item.problem === 'price-missing' || item.priceKopecks === null) return NO_PRICE_REASON
  if (item.problem !== null) return `Товар нельзя продать (${item.problem})`
  // Ответ не по договору (неизвестный код, не тот тип): значение не подставляется, строка блокируется словами о том, что не так.
  if (item.decodeIssues.length > 0) return `Служба прислала кассовые реквизиты товара не по договору (${item.decodeIssues.join(', ')}). Товар нельзя продать`
  // Служба сама заблокировала строку: её слова главнее и показываются дословно (ADR-8), даже если клиент причину не знает.
  if (item.state === 'blocked') {
    if (item.blockMessage !== null) return item.blockMessage
    if (item.blockReasons.length > 0) return `Товар нельзя продать: ${item.blockReasons.map((reason) => serverReasonText(reason, item.undefinedFields)).join(', ')}`
  }
  // Дальше — страховка клиента на случай, когда служба строку не заблокировала, а продавать её всё же нельзя.
  switch (item.vatState) {
    case 'missing':
      return NO_VAT_REASON
    case 'unmapped':
      return UNMAPPED_VAT_REASON
    case 'unavailable':
      return UNAVAILABLE_VAT_REASON
    case 'set':
      if (item.vatRateCode === null || !isVatCode(item.vatRateCode)) return UNMAPPED_VAT_REASON
      break
  }
  if (item.state === 'blocked') return 'Служба не разрешила продажу этого товара'
  const reasons = profileBlockReasons(item.profile)
  return reasons.length === 0 ? null : profileReason(reasons)
}

/** Ключ сообщения об отказе продать товар: следующий отказ заменяет прежний, а не копит их стопкой. */
export const REFUSED_NOTICE_KEY = 'sale-refused'

/**
 * RP-SY5: сообщение кассиру, что выбранный, отсканированный или найденный по Enter товар продать нельзя, — причина дословно, с
 * названием и артикулом, чтобы её можно было скопировать и передать; `null` — товар продаётся.
 */
export function refusedNoticeOf(row: PickerRowVm): OwnNotice | null {
  if (row.available) return null
  const what = row.sku === null ? `«${row.name}»` : `«${row.name}» (${row.sku})`
  return { key: REFUSED_NOTICE_KEY, text: `${what} нельзя продать: ${row.reason ?? 'служба не разрешила продажу этого товара'}`, tone: 'warning', action: null }
}

/** Строка выдачи поиска: цена и причина. */
export function saleLineOf(item: OwnSaleLineItem): OwnSaleLine {
  return {
    productKey: item.cardKey,
    priceText: item.priceKopecks === null ? '—' : formatKopecks(item.priceKopecks),
    unit: item.unit,
    isWeight: item.isWeight,
    blockedReason: blockedReasonOf(item),
  }
}

/** Товар для корзины; `null`, если продавать нельзя. Профиль карточки переносится как есть; неизвестный «весовой» строку блокирует, а не делает штучной. */
export function saleProductOf(item: OwnSaleLineItem): SaleProduct | null {
  if (blockedReasonOf(item) !== null || item.priceKopecks === null || !isVatCode(item.vatRateCode)) return null
  return {
    key: item.cardKey,
    name: item.name,
    sku: item.sku,
    barcode: item.barcode,
    priceKopecks: item.priceKopecks,
    vat: item.vatRateCode,
    unit: item.unit,
    isWeight: item.profile?.isWeighted === true,
    profile: item.profile,
  }
}